import { useState, useEffect, useRef } from 'react';
import { Chart, registerables } from 'chart.js';
import { HiOutlineCurrencyDollar } from 'react-icons/hi';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchDashboardOverview,
  setActiveFilter,
  selectDashboardLoading,
  selectDashboardError,
  selectActiveFilter,
  selectStats,
  selectBudgetAnalysis,
  selectSpendTrend,
  selectSpendByCategory,
  selectTransactions,
} from '../../../store/dashboardSlice';
import {
  fetchBuyerEngagementWidgets,
  selectEngagementHours,
  selectDirectoryBreakdown,
  selectAiBreakdown,
  selectEngagementLoading,
  selectEngagementError,
} from '../../../store/engagementAnalyticsSlice';

Chart.register(...registerables);

const formatMoney = (num) => {
  if (num == null) return '$0';
  return '$' + Number(num).toLocaleString('en-US');
};

const colors = {
  primary: '#0088ff',
  text: '#1d2739',
  subtext: '#7b7b7b',
  border: '#ebebeb',
  rowBg: '#f7f7f7',
  green: '#02ad28',
  greenBg: 'rgba(180,246,195,0.1)',
  orange: '#ad4402',
  orangeBg: 'rgba(246,208,180,0.1)',
  chartBlue: '#3b82f6',
  chartPink: '#ec4899',
  chartTeal: '#14b8a6',
  chartOrange: '#f97316',
  chartPurple: '#8b5cf6',
  chartYellow: '#eab308',
};

const CHART_PALETTE = [
  colors.chartBlue,
  colors.chartPink,
  colors.chartTeal,
  colors.chartOrange,
  colors.chartPurple,
  colors.chartYellow,
  '#06b6d4',
  '#f43f5e',
  '#84cc16',
  '#a78bfa',
];

const dirCols = {
  directory: [
    { key: 'dept', label: 'Department' },
    { key: 'profiles', label: 'Profiles viewed' },
    { key: 'searches', label: 'Searches Performed' },
    { key: 'matches', label: 'Matches returned' },
  ],
  ai: [
    { key: 'dept', label: 'Department' },
    { key: 'profiles', label: 'Matching calls' },
    { key: 'matches', label: 'Avg. inference latency' },
  ],
};

function FilterTabs({ active, setActive }) {
  const tabs = ['This Week', 'This Month', 'This Year'];
  return (
    <div className="flex rounded-xl border p-1">
      {tabs.map((t, i) => (
        <button
          key={t}
          onClick={() => setActive(t)}
          className={`whitespace-nowrap px-4 py-1 text-sm ${i < 2 ? 'border-r' : ''} ${
            active === t ? 'font-semibold text-[#0088FF]' : 'text-gray-600 hover:text-gray-400'
          }`}
        >
          {t}
        </button>
      ))}
    </div>
  );
}

function SectionTitle({ children }) {
  return <div className="mb-6 text-base font-semibold text-[#1D2739] text-left">{children}</div>;
}

function StatCards({ isUser, stats }) {
  if (!stats) {
    return (
      <div
        className={`grid grid-cols-1 gap-2 md:grid-cols-2 ${!isUser ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}
      >
        {[...Array(isUser ? 3 : 4)].map((_, i) => (
          <div
            key={i}
            className="w-full rounded-lg border border-[#EBEBEB] bg-white px-[11px] pb-3 pt-[9px] animate-pulse shadow-[0_3px_0_rgba(0,0,0,0.1)]"
          >
            <div className="h-9 w-24 bg-gray-100 rounded mb-2" />
            <div className="h-4 w-40 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const adminStats = [
    {
      value: stats.totalEnterpriseSuppliers?.toLocaleString() ?? '—',
      label: 'Total Suppliers (Enterprise)',
    },
    {
      value: stats.totalManagedSuppliers?.toLocaleString() ?? '—',
      label: 'Total Suppliers (Admin)',
    },
    { value: stats.tenantStats?.totalTenants?.toLocaleString() ?? '—', label: 'Total Tenants' },
    { value: stats.tenantStats?.activeTenants?.toLocaleString() ?? '—', label: 'Active Tenants' },
  ];

  const userStats = [
    {
      value: stats.totalManagedSuppliers?.toLocaleString() ?? '—',
      label: 'Total Suppliers (Admin)',
    },
    { value: stats.tier1Count, label: 'Tier 1 Suppliers' },
    { value: stats.tier2Count, label: 'Tier 2 Suppliers' },
  ];

  const list = isUser ? userStats : adminStats;

  return (
    <div
      className={`grid grid-cols-1 gap-2 md:grid-cols-2 ${!isUser ? 'xl:grid-cols-4' : 'xl:grid-cols-3'}`}
    >
      {list.map((s) => (
        <div
          key={s.label}
          className="w-full min-w-0 rounded-lg border border-[#EBEBEB] bg-white px-[11px] pb-3 pt-[9px] text-left shadow-[0_3px_0_rgba(0,0,0,0.1)]"
        >
          <div className="text-[32px] font-medium leading-[1.3] text-[#484848]">{s.value}</div>
          <div className="mt-[7px] text-base text-[#7B7B7B]">{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function BudgetCard({ title, amount, badge, footer }) {
  return (
    <div className="flex flex-1 flex-col justify-start items-start gap-2 rounded-2xl border border-[#EBEBEB] bg-white px-4 py-2">
      <div className="text-xl text-[#0088FF]">
        <HiOutlineCurrencyDollar className="text-[#0088FF] text-xl" />
      </div>
      <div className="text-xl font-normal text-black">{title}</div>
      <div className="flex items-baseline gap-2">
        <span className="text-[36px] font-semibold leading-none text-[#1D2739]">{amount}</span>
        <span className="text-[24px] font-normal text-[#1D2739]">USD</span>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-base text-[#7E7E7E]">Since last period</span>
        <span
          className={`rounded-[19px] px-4 py-2 text-base ${
            badge === 'Within Budget'
              ? 'bg-[rgba(180,246,195,0.1)] text-[#02AD28]'
              : 'bg-[rgba(220,38,38,0.08)] text-[#DC2626]'
          }`}
        >
          {badge}
        </span>
      </div>
      <div className="border-t border-[#EBEBEB] pt-2 w-full" />
      <div className="text-base font-medium text-[#1D2739]">{footer}</div>
    </div>
  );
}

function BudgetSkeleton() {
  return (
    <div className="flex flex-1 flex-col gap-2 rounded-2xl border border-[#EBEBEB] bg-white px-4 py-4 animate-pulse">
      <div className="h-5 w-5 bg-gray-100 rounded" />
      <div className="h-5 w-32 bg-gray-100 rounded" />
      <div className="h-10 w-40 bg-gray-100 rounded" />
      <div className="h-4 w-48 bg-gray-100 rounded" />
    </div>
  );
}

function SpendTrendChart({ spendTrend }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!spendTrend || !canvasRef.current) return;

    const { series } = spendTrend;
    const capped = series.slice(0, 10);

    const labelSet = new Set();
    const allLabels = [];
    for (const s of capped) {
      for (const d of s.data) {
        if (!labelSet.has(d.period)) {
          labelSet.add(d.period);
          allLabels.push(d.period);
        }
      }
    }

    const datasets = capped.map((s, i) => {
      const map = Object.fromEntries(s.data.map((d) => [d.period, d.amount]));
      return {
        label: s.name,
        data: allLabels.map((l) => map[l] ?? 0),
        borderColor: CHART_PALETTE[i % CHART_PALETTE.length],
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.35,
        fill: false,
      };
    });

    const legendPaddingPlugin = {
      id: 'legendPadding',
      beforeInit(chart) {
        const originalFit = chart.legend.fit.bind(chart.legend);
        chart.legend.fit = function () {
          originalFit();
          this.height += 20;
        };
      },
    };

    const ctx = canvasRef.current.getContext('2d');
    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: { labels: allLabels, datasets },
      plugins: [legendPaddingPlugin],
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: { top: 8 },
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            align: 'start',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              pointStyleWidth: 10,
              boxHeight: 8,
              font: { family: "'Hanken Grotesk', sans-serif", size: 12 },
              color: colors.text,
              padding: 20,
            },
          },
          tooltip: { mode: 'index', intersect: false },
        },
        scales: {
          x: {
            grid: { display: true, color: '#f0f0f0' },
            ticks: {
              font: { family: "'Hanken Grotesk', sans-serif", size: 12 },
              color: colors.subtext,
            },
          },
          y: {
            grid: { color: '#f0f0f0' },
            ticks: {
              font: { family: "'Hanken Grotesk', sans-serif", size: 12 },
              color: colors.subtext,
              callback: (v) => `${v / 1000}K`,
            },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [spendTrend]);

  return (
    <div
      className="relative min-w-0 rounded-[15px] border border-[#EBEBEB] bg-white p-6"
      style={{ height: 420 }}
    >
      {!spendTrend ? (
        <div className="h-full w-full animate-pulse bg-gray-50 rounded-lg" />
      ) : (
        <canvas ref={canvasRef} />
      )}
    </div>
  );
}

function SpendByCategory({ spendByCategory }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!spendByCategory || !canvasRef.current) return;

    const { categories } = spendByCategory;
    const labels = categories.map((c) => c.category);
    const data = categories.map((c) => c.amount);

    const ctx = canvasRef.current.getContext('2d');
    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, {
      type: 'doughnut',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: CHART_PALETTE.slice(0, labels.length),
            borderWidth: 2,
            borderColor: '#fff',
            hoverOffset: 6,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              usePointStyle: true,
              pointStyle: 'circle',
              font: { family: "'Hanken Grotesk', sans-serif", size: 12 },
              color: colors.text,
              padding: 12,
            },
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [spendByCategory]);

  return (
    <div className="flex h-[420px] w-full min-w-0 flex-col gap-2 rounded-2xl border border-[#EBEBEB] bg-white p-6 xl:w-[376px]">
      <div className="text-base text-left font-semibold text-[#1D2739]">
        Spend by Category <span className="font-normal text-[#9B9B9B]"></span>
      </div>
      <div className="relative min-h-0 flex-1">
        {!spendByCategory ? (
          <div className="h-full w-full animate-pulse bg-gray-50 rounded-full" />
        ) : (
          <canvas ref={canvasRef} />
        )}
      </div>
    </div>
  );
}

function SpendBySupplierChart({ spendByCategory }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  useEffect(() => {
    if (!spendByCategory || !canvasRef.current) return;

    const { categories } = spendByCategory;
    const labels = categories.map((c) => c.category);
    const data = categories.map((c) => c.percentage);

    const ctx = canvasRef.current.getContext('2d');
    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: CHART_PALETTE.slice(0, labels.length),
            borderRadius: 4,
            borderSkipped: false,
            barThickness: 24,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: { callbacks: { label: (ctx) => `${ctx.parsed.y}%` } },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { family: "'Hanken Grotesk', sans-serif", size: 11 },
              color: colors.subtext,
            },
          },
          y: {
            grid: { color: '#f0f0f0' },
            ticks: {
              font: { family: "'Hanken Grotesk', sans-serif", size: 12 },
              color: colors.subtext,
              callback: (v) => `${v}%`,
            },
            min: 0,
            max: 100,
          },
        },
      },
    });

    return () => chartRef.current?.destroy();
  }, [spendByCategory]);

  return (
    <div className="rounded-2xl border border-[#EBEBEB] bg-white px-6 pb-4 pt-6">
      <div className="mb-4 text-left text-base font-semibold text-[#1D2739]">
        Spend by Category (%)
      </div>
      <div className="h-[295px]">
        {!spendByCategory ? (
          <div className="h-full w-full animate-pulse bg-gray-50 rounded-lg" />
        ) : (
          <canvas ref={canvasRef} />
        )}
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const badgeClass =
    {
      Pending: 'bg-[rgba(246,208,180,0.1)] text-[#AD4402]',
      Approved: 'bg-[rgba(180,246,195,0.1)] text-[#02AD28]',
      Materials: 'bg-[rgba(180,246,195,0.1)] text-[#02AD28]',
      Technology: 'bg-[rgba(0,136,255,0.08)] text-[#0088FF]',
      Services: 'bg-[rgba(0,136,255,0.08)] text-[#0088FF]',
      Failed: 'bg-[rgba(220,38,38,0.08)] text-[#DC2626]',
    }[status] || 'bg-[rgba(246,208,180,0.1)] text-[#AD4402]';

  return (
    <span className={`whitespace-nowrap rounded-[19px] px-[14px] py-[6px] text-sm ${badgeClass}`}>
      {status}
    </span>
  );
}

function TransactionTable({ activeFilter, setActiveFilter, transactions = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const gridCols =
    'grid-cols-[minmax(145px,0.9fr)_minmax(190px,1.3fr)_minmax(120px,0.8fr)_minmax(220px,1.4fr)_minmax(110px,0.7fr)]';

  const formatTransactionDate = (date) => {
    if (!date) return 'N/A';

    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const apiTransactions = transactions.map((item) => ({
    date: item.date,
    desc: item.description,
    category: item.category,
    supplier: item.supplierName,
    amount: item.amount,
  }));

  const filteredTransactions = apiTransactions.filter((row) => {
    const search = searchValue.toLowerCase();
    return Object.values(row).some((v) => String(v).toLowerCase().includes(search));
  });

  const TableHeader = () => (
    <div
      className={`grid h-[48px] ${gridCols} items-center gap-3 bg-[#F7F7F7] px-5 text-sm text-[#1D2739]`}
    >
      <div>Date</div>
      <div>Description</div>
      <div>Category</div>
      <div>Supplier</div>
      <div>Amount</div>
    </div>
  );

  const TableRow = ({ row, i }) => (
    <div
      className={`grid h-[48px] ${gridCols} items-center gap-3 px-5 text-sm text-[#1D2739] ${
        i > 0 ? 'border-t border-[#F0F0F0]' : ''
      }`}
    >
      <div className="truncate">{formatTransactionDate(row.date)}</div>
      <div className="truncate">{row.desc || 'N/A'}</div>
      <div className="truncate">{row.category || 'N/A'}</div>
      <div className="truncate">{row.supplier || 'Unknown'}</div>
      <div className="text-[#0088FF]">{formatMoney(row.amount)}</div>
    </div>
  );

  return (
    <>
      <div className="flex w-full min-w-0 flex-col gap-6">
        <div className="flex items-center justify-between">
          <div className="text-left">
            <SectionTitle>Detailed Transaction Data</SectionTitle>
          </div>

          <button
            onClick={() => setIsOpen(true)}
            className="rounded-[5px] bg-[rgba(0,136,255,0.08)] px-3 py-2 text-base font-medium text-[#0088FF]"
          >
            View All
          </button>
        </div>

        <div className="w-full min-w-0 overflow-hidden rounded-3xl border border-[#F7F7F7] bg-white">
          <TableHeader />

          {filteredTransactions.slice(0, 5).map((row, i) => (
            <TableRow key={`${row.date}-${row.desc}-${i}`} row={row} i={i} />
          ))}

          {filteredTransactions.length === 0 && (
            <div className="px-5 py-6 text-sm text-[#7B7B7B]">
              No transactions found for this period.
            </div>
          )}
        </div>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-hidden bg-black/30 px-4">
          <div className="flex h-[88vh] w-full max-w-[calc(100vw-132px)] flex-col overflow-hidden rounded-[24px] bg-white p-6 shadow-xl">
            <div className="mb-5 flex shrink-0 items-center justify-between gap-4">
              <div>
                <h2 className="text-left text-xl font-semibold text-[#1D2739]">
                  Detailed Transaction Data
                </h2>
                <p className="mt-1 text-left text-sm text-[#7B7B7B]">
                  View, search, and filter all transaction records.
                </p>
              </div>

              <button
                onClick={() => setIsOpen(false)}
                className="flex h-12 w-12 items-center justify-center rounded-full text-3xl text-[#7B7B7B] hover:text-[#484848]"
              >
                ×
              </button>
            </div>

            <div className="mb-5 flex items-center justify-between gap-4">
              <div className="flex w-full max-w-[350px] items-center gap-3 rounded-xl bg-[#F3F3F3] px-4 py-3">
                <input
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  placeholder="Type to search transactions..."
                  className="w-full bg-transparent text-sm text-[#1D2739] placeholder:text-[#9CA3AF] focus:outline-none"
                />
              </div>

              <FilterTabs active={activeFilter} setActive={setActiveFilter} />
            </div>

            <div className="min-h-0 flex-1 overflow-hidden rounded-3xl border border-[#F7F7F7] bg-white">
              <div className="h-full overflow-y-auto">
                <div className="sticky top-0 z-10">
                  <TableHeader />
                </div>

                {filteredTransactions.map((row, i) => (
                  <TableRow key={`${row.date}-${row.desc}-${i}`} row={row} i={i} />
                ))}

                {filteredTransactions.length === 0 && (
                  <div className="px-5 py-6 text-sm text-[#7B7B7B]">
                    No transactions found for this period.
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function EngagementBarChart({ hours, loading }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);

  const labels = hours?.labels ?? [];
  const values = hours?.values ?? [];
  const maxValue = values.length ? Math.max(...values, 1) * 1.1 : 90;

  useEffect(() => {
    if (!canvasRef.current || loading) return;

    const ctx = canvasRef.current.getContext('2d');
    chartRef.current?.destroy();
    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            data: values,
            backgroundColor: CHART_PALETTE,
            borderRadius: 4,
            borderSkipped: false,
            barThickness: 24,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              font: { family: "'Hanken Grotesk', sans-serif", size: 12 },
              color: colors.subtext,
            },
          },
          y: {
            grid: { color: '#f0f0f0' },
            ticks: {
              font: { family: "'Hanken Grotesk', sans-serif", size: 12 },
              color: colors.subtext,
              callback: (v) => `${v}h`,
            },
            min: 0,
            max: maxValue,
          },
        },
      },
    });
    return () => chartRef.current?.destroy();
  }, [labels, values, loading, maxValue]);

  return (
    <div className="rounded-2xl border border-[#EBEBEB] bg-white px-6 pb-4 pt-6">
      <div className="mb-4 text-base font-semibold text-[#1D2739] text-left">
        Supplier Engagement in hrs (Global)
      </div>
      <div className="h-[350px]">
        {loading ? (
          <div className="h-full w-full animate-pulse bg-gray-50 rounded-lg" />
        ) : labels.length === 0 ? (
          <div className="flex h-full items-center justify-center text-sm text-[#7E7E7E]">
            No engagement hours data for this period
          </div>
        ) : (
          <canvas ref={canvasRef} />
        )}
      </div>
    </div>
  );
}

function DirectoryTable({ title, cols, rows, loading, emptyMessage }) {
  const navigate = useNavigate();
  return (
    <div className="flex flex-1 flex-col gap-2">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-base font-semibold leading-[1.4] text-[#1D2739]">{title}</span>
        <button
          className="rounded-[5px] bg-[rgba(0,136,255,0.08)] px-[10px] py-[6px] text-sm font-medium text-[#0088FF]"
          onClick={() => navigate('/dashboard/admin/engagement')}
        >
          View All
        </button>
      </div>
      <div className="overflow-hidden rounded-2xl border border-[#F7F7F7] bg-white">
        <div
          className="grid h-11 bg-[#F7F7F7]"
          style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}
        >
          {cols.map((c) => (
            <div
              key={c.key}
              className="flex items-center justify-start whitespace-nowrap px-2 text-sm text-[#1D2739]"
            >
              {c.label}
            </div>
          ))}
        </div>
        {loading ? (
          <div className="px-4 py-8 text-center text-sm text-[#7E7E7E]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-sm text-[#7E7E7E]">{emptyMessage}</div>
        ) : (
          rows.map((row, i) => (
            <div
              key={`${row.dept}-${i}`}
              className={`grid h-16 ${i > 0 ? 'border-t border-[#F0F0F0]' : ''}`}
              style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}
            >
              {cols.map((c) => (
                <div
                  key={c.key}
                  className="flex items-center justify-center px-2 text-center text-sm text-[#1D2739]"
                >
                  {row[c.key]}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="mb-6 flex items-center justify-between rounded-xl border border-red-100 bg-red-50 px-5 py-3 text-sm text-red-600">
      <span>⚠ {message}</span>
      <button
        onClick={onRetry}
        className="ml-4 rounded-lg bg-red-100 px-3 py-1 font-medium hover:bg-red-200"
      >
        Retry
      </button>
    </div>
  );
}

export default function Dashboard() {
  const dispatch = useDispatch();
  const { role } = useSelector((s) => s.auth);
  const isUser = role === 'USER';

  const loading = useSelector(selectDashboardLoading);
  const error = useSelector(selectDashboardError);
  const activeFilter = useSelector(selectActiveFilter);
  const stats = useSelector(selectStats);
  const budgetAnalysis = useSelector(selectBudgetAnalysis);
  const spendTrend = useSelector(selectSpendTrend);
  const spendByCategory = useSelector(selectSpendByCategory);
  const transactions = useSelector(selectTransactions);
  const engagementHours = useSelector(selectEngagementHours);
  const directoryBreakdown = useSelector(selectDirectoryBreakdown);
  const aiBreakdown = useSelector(selectAiBreakdown);
  const engagementLoading = useSelector(selectEngagementLoading);
  const engagementError = useSelector(selectEngagementError);

  useEffect(() => {
    dispatch(fetchDashboardOverview(activeFilter));
    dispatch(fetchBuyerEngagementWidgets(activeFilter));
  }, [dispatch, activeFilter]);

  const handleFilterChange = (filter) => {
    dispatch(setActiveFilter(filter));
  };

  const aggregationLabel =
    activeFilter === 'This Week'
      ? 'Aggregated by week'
      : activeFilter === 'This Year'
        ? 'Aggregated by year'
        : 'Aggregated by day';

  const directoryRows = directoryBreakdown.map((row) => ({
    dept: row.department,
    profiles: row.profilesViewed,
    searches: row.searchesPerformed,
    matches: row.matchesReturned,
  }));

  const aiRows = aiBreakdown.map((row) => ({
    dept: row.department,
    profiles: row.matchingCalls,
    matches: row.avgInferenceLatency,
  }));

  return (
    <div className="min-h-screen w-full max-w-full overflow-x-hidden bg-white p-5 font-['Hanken_Grotesk'] text-[#1D2739]">
      <div className="mb-10 flex items-center justify-between mt-4">
        <h1 className="m-0 text-[28px] font-semibold text-[#1D2739]">Overview</h1>
        <div className="flex items-center gap-4">
          {loading && <span className="text-sm text-[#7B7B7B] animate-pulse">Loading…</span>}
          <FilterTabs active={activeFilter} setActive={handleFilterChange} />
        </div>
      </div>

      {error && (
        <ErrorBanner
          message={error}
          onRetry={() => dispatch(fetchDashboardOverview(activeFilter))}
        />
      )}

      <div className="mb-10">
        <StatCards isUser={isUser} stats={stats} />
      </div>

      <div className="text-left">
        <SectionTitle>Budget Analysis</SectionTitle>
      </div>
      <div className="flex flex-row flex-wrap items-start gap-6 mb-8">
        {!budgetAnalysis ? (
          <>
            <BudgetSkeleton />
            <BudgetSkeleton />
          </>
        ) : (
          <>
            <BudgetCard
              title="Total Budget"
              amount={formatMoney(budgetAnalysis.totalBudget)}
              badge={budgetAnalysis.statusBudget}
              footer={aggregationLabel}
            />
            <BudgetCard
              title="Actual Spending"
              amount={formatMoney(budgetAnalysis.actualSpend)}
              badge={budgetAnalysis.statusBudget}
              footer={aggregationLabel}
            />
          </>
        )}
      </div>

      <div className="text-left">
        <SectionTitle>Spend trend analysis</SectionTitle>
      </div>

      <div
        className={`mb-8 grid w-full gap-4 ${
          isUser ? 'grid-cols-1' : 'grid-cols-1 xl:grid-cols-[minmax(0,1fr)_376px]'
        }`}
      >
        <SpendTrendChart spendTrend={spendTrend} />
        {!isUser && <SpendByCategory spendByCategory={spendByCategory} />}
      </div>

      <div className="mb-8">
        <SpendBySupplierChart spendByCategory={spendByCategory} />
      </div>

      <div className="mb-8">
        <TransactionTable
          activeFilter={activeFilter}
          setActiveFilter={handleFilterChange}
          transactions={transactions}
        />
        <div className="mb-8" />
      </div>

      {engagementError && (
        <ErrorBanner
          message={engagementError}
          onRetry={() => dispatch(fetchBuyerEngagementWidgets(activeFilter))}
        />
      )}

      <div className="mb-8">
        <EngagementBarChart hours={engagementHours} loading={engagementLoading} />
      </div>

      {!isUser && (
        <div className="mb-8 grid w-full grid-cols-1 gap-8 xl:grid-cols-2">
          <DirectoryTable
            title="Supplier Directory Engagement (Global)"
            cols={dirCols.directory}
            rows={directoryRows}
            loading={engagementLoading}
            emptyMessage="No directory engagement data for this period"
          />
          <DirectoryTable
            title="AI Engagement Metrics (Tenant-Scoped, Aggregated)"
            cols={dirCols.ai}
            rows={aiRows}
            loading={engagementLoading}
            emptyMessage="No AI engagement data for this period"
          />
        </div>
      )}
    </div>
  );
}
